import { useCallback } from "react";

const GA_MEASUREMENT_ID = "G-X30D192VT7";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let isInitialized = false;

export function initializeGA() {
  if (isInitialized) {
    return;
  }

  // Initialize dataLayer and gtag function FIRST (before loading script)
  // This matches Google's recommended implementation exactly
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // Use arguments object like Google's recommendation
    window.dataLayer.push(arguments);
  };

  // Set js date immediately
  window.gtag("js", new Date());

  // Configure GA immediately (will queue in dataLayer until script loads)
  // This matches Google's recommended pattern
  window.gtag("config", GA_MEASUREMENT_ID, {
    // Anonymize IP for GDPR compliance
    anonymize_ip: true,
    // Send page view on initialization
    send_page_view: true,
  });

  // Create and load script element for gtag.js
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  isInitialized = true;

  // Track initial page view
  trackPageView("/", "Home");
}

export function trackPageView(path: string, title: string) {
  if (!isInitialized || !window.gtag) {
    return;
  }

  try {
    // Use proper GA4 page_view event
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    });
  } catch (error) {
    console.error("Failed to track page view:", error);
  }
}

export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (!isInitialized || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", eventName, params);
  } catch (error) {
    console.error("Failed to track event:", eventName, error);
  }
}

export function useAnalytics() {
  const trackSlideView = useCallback((slideIndex: number, slideName: string) => {
    trackEvent("slide_view", {
      slide_index: slideIndex,
      slide_name: slideName,
      event_category: "navigation",
      event_label: slideName,
    });
  }, []);

  const trackOutboundLink = useCallback((url: string, linkName: string) => {
    trackEvent("click", {
      event_category: "outbound",
      event_label: linkName,
      link_url: url,
    });
  }, []);

  const trackDuckQuack = useCallback(() => {
    trackEvent("duck_quack", {
      event_category: "engagement",
      event_label: "header_duck",
    });
  }, []);

  const trackDocumentationClick = useCallback((context: string) => {
    trackEvent("documentation_nav_click", {
      event_category: "navigation",
      event_label: context,
    });
  }, []);

  const trackGithubClick = useCallback((context: string) => {
    trackEvent("github_cta_click", {
      event_category: "outbound",
      event_label: context,
      link_url: "https://github.com/kuack-io/kuack",
    });
  }, []);

  const trackContactClick = useCallback((address: string) => {
    trackEvent("contact_link_click", {
      event_category: "engagement",
      event_label: address,
    });
  }, []);

  const trackImpressumClick = useCallback(() => {
    trackEvent("impressum_open", {
      event_category: "legal",
      event_label: "footer_impressum",
    });
  }, []);

  const trackHeroCta = useCallback((ctaName: string, destination: string) => {
    trackEvent("hero_cta_click", {
      event_category: "cta",
      event_label: ctaName,
      destination,
    });
  }, []);

  return {
    trackSlideView,
    trackOutboundLink,
    trackDuckQuack,
    trackDocumentationClick,
    trackGithubClick,
    trackContactClick,
    trackImpressumClick,
    trackHeroCta,
    trackEvent,
  };
}
